import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { styled } from "@mui/material";
import { flushSync } from "react-dom";
import type { StrictOmit } from "ts-essentials";
import type { Maybe } from "~/types";

const Root = styled("div")({
  width: "100%",
  overflowX: "hidden",
  overflowY: "auto",
  position: "relative",
});

const List = styled("ul")({
  listStyle: "none",
  margin: 0,
  padding: 0,
});

const ListItem = styled("li")({
  width: "100%",
  position: "absolute",
  left: 0,
  margin: 0,
  padding: 0,
});

type MeasurementMap = Map<React.Key, number>;
type ReadonlyMeasurementMap = ReadonlyMap<React.Key, number>;

interface VirtualizedListState {
  /**
   * Last scrollport width received as a prop.
   */
  scrollportWidth: number;
  /**
   * Measured height of a single line of text. Used as a placeholder height for
   * items which haven't yet been measured.
   */
  lineHeightPx: number;
  /**
   * Starting position of the scrollport.
   */
  scrollTopPx: number;
  /**
   * Item height measurements from before the last change to `scrollportWidth`.
   * Used to keep the container height mostly stable when `scrollportWidth`
   * changes by using the last known height of each item rather than the default
   * value of `lineHeightPx`. As new measurements are taken and stored in
   * `measurements`, they are removed from this map if present. Consequently,
   * this map is disjoint from `measurements`.
   */
  previousMeasurements: ReadonlyMeasurementMap;
  /**
   * Item height measurements known to currently be accurate. As new items are
   * materialized and measured, their heights are stored in this map for future
   * renders to more-accurately calculate the list's height as well as position
   * materialized items in the scrollport. As new measurements are taken and
   * stored in this map, they are removed from `previousMeasurements` if
   * present. Consequently, this map is disjoint from `previousMeasurements`.
   */
  measurements: ReadonlyMeasurementMap;
  /**
   * Last pinned item index received as a prop.
   */
  pinnedItemIndex: number | undefined;
  /**
   * Last repin key received as a prop.
   */
  repinKey: React.Key | undefined;
}

const initialState: StrictOmit<VirtualizedListState, "scrollportWidth"> = {
  lineHeightPx: 0,
  scrollTopPx: 0,
  previousMeasurements: new Map(),
  measurements: new Map(),
  pinnedItemIndex: undefined,
  repinKey: undefined,
};

export function VirtualizedList<TItem>({
  width: scrollportWidth,
  height: scrollportHeight,
  items,
  pinnedItemIndex,
  repinKey,
  prerenderCount = 0,
  getItemKey,
  renderItem,
}: {
  width: number;
  height: number;
  items: ReadonlyArray<TItem>;
  pinnedItemIndex?: number;
  repinKey?: React.Key;
  prerenderCount?: number;
  getItemKey: (item: NoInfer<TItem>) => React.Key;
  renderItem: (item: NoInfer<TItem>, index: number) => React.ReactNode;
}) {
  const [listState, setListState] = useState<VirtualizedListState>({
    scrollportWidth,
    ...initialState,
  });

  const rootRef = useRef<HTMLDivElement>(null);
  const listItemsRef = useRef(new Map<React.Key, HTMLLIElement>());

  // Pin state changing doesn't guarantee we need to re-pin anything but it
  // does indicate we'll need to update our state.
  const didPinChange =
    repinKey !== listState.repinKey ||
    pinnedItemIndex !== listState.pinnedItemIndex;
  const isPinNeeded =
    didPinChange &&
    pinnedItemIndex != null &&
    0 <= pinnedItemIndex &&
    pinnedItemIndex < items.length;

  const { totalHeight, scrollTopOverride, bottomOffset, materializedItems } =
    processItems(
      listState,
      scrollportHeight,
      items,
      isPinNeeded ? pinnedItemIndex : null,
      prerenderCount,
      getItemKey,
    );

  function handleScroll(e: React.UIEvent<HTMLDivElement>): void {
    const {
      currentTarget: { scrollTop },
    } = e;

    // Momentum scrolling doesn't work on iPhone and iPad Safari if this update
    // isn't wrapped in `flushSync`. Probably has something to do with React
    // deferring the state update until the next microtask checkpoint if not
    // wrapped in `flushSync`.
    flushSync(() => {
      setListState((prev) => {
        if (scrollTop === prev.scrollTopPx) {
          return prev;
        } else {
          return {
            ...prev,
            scrollTopPx: scrollTop,
          };
        }
      });
    });
  }

  // When the component first mounts, its stored line height is 0 to indicate
  // a measurement actually needs to be taken. After that items can actually
  // start to be materialized and rendered.
  const measureLineHeight = listState.lineHeightPx === 0;

  useLayoutEffect(
    function measureSentinelHeight() {
      if (!measureLineHeight) {
        return;
      }

      const sentinel = rootRef.current!.querySelector(
        "[data-sentinel]",
      ) as HTMLLIElement;

      const { height: lineHeightPx } = sentinel.getBoundingClientRect();

      if (lineHeightPx === 0) {
        return;
      }

      setListState((prev) => ({
        ...prev,
        lineHeightPx,
      }));
    },
    [measureLineHeight],
  );

  // Not including a dependency array - against the linter's recommendation -
  // because this Effect is careful to only update state when things actually
  // change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (measureLineHeight) {
      return;
    }

    const hasWidthChanged = scrollportWidth !== listState.scrollportWidth;

    let newPreviousMeasurements: MeasurementMap | null = null;
    let newMeasurements: MeasurementMap | null = null;
    // As materialized items are measured it's possible their actual height will
    // be different from the default height or their previous measurement (if
    // the scrollport width changed). These changes will lead to a change in the
    // root's height but the `scrollTop` doesn't automatically adjust. To keep
    // the scrollport's contents stable as new measurements are taken
    // (basically, maintain whatever's at the top of the scrollport), the root's
    // `scrollTop` as well as the stored `scrollTopPx` needs to be adjusted by
    // the amount the container is growing or shrinking.
    let scrollTopAdjustment = -bottomOffset;
    if (hasWidthChanged) {
      setListState((prev) => ({
        ...prev,
        scrollportWidth,
      }));

      // Since the scrollport's width has changed, any height measurement could
      // now be different. However, clearing all measurements could drastically
      // reduce the calculated list height. The scrollport will still be
      // populated correctly since we'll go ahead and measure the materialized
      // items again, but we're taking the approach of trying to keep the
      // height and scrollbar position mostly stable too by pretending any
      // item's height is the same. As the scrollport moves around we'll take
      // actual measurements and ditch previous measurements which will get us
      // closer to the true height of the list.
      newPreviousMeasurements = new Map(listState.previousMeasurements);
      // All current measurements need to be merged in to the existing set of
      // previous measurements since they're now stale.
      for (const [key, measurement] of listState.measurements) {
        newPreviousMeasurements.set(key, measurement);
      }

      newMeasurements = new Map();

      for (const [key, listItem] of listItemsRef.current) {
        const { height } = listItem.getBoundingClientRect();

        const previousMeasurement = newPreviousMeasurements.get(key);

        // The current and previous measurements need to be disjoint. Since
        // we have a new measurement for this list item, we can delete a
        // previous measurement if it existed.
        newPreviousMeasurements.delete(key);
        newMeasurements.set(key, height);

        if (
          listItem.dataset.prerender === "before" &&
          previousMeasurement != null
        ) {
          scrollTopAdjustment += height - previousMeasurement;
        }
      }
    } else {
      for (const [key, listItem] of listItemsRef.current) {
        if (listState.measurements.has(key)) {
          continue;
        }

        newPreviousMeasurements ??= new Map(listState.previousMeasurements);
        newMeasurements ??= new Map(listState.measurements);

        const { height } = listItem.getBoundingClientRect();

        // The current and previous measurements need to be disjoint. Since
        // we have a new measurement for this list item, we can delete a
        // previous measurement if it existed.
        newPreviousMeasurements.delete(key);
        newMeasurements.set(key, height);

        if (
          listItem.dataset.prerender === "before" ||
          // Typically we only care about items rendered above the scrollport
          // since only they can affect `scrollTop`. However, when an item is
          // being pinned to the bottom of the viewport, it's important to also
          // account for any changes to items within the scrollport since their
          // updated heights will push or pull the pinned item away from the
          // bottom edge of the scrollport.
          (isPinNeeded && listItem.dataset.prerender !== "after")
        ) {
          const previousHeight =
            listState.previousMeasurements.get(key) ?? listState.lineHeightPx;

          scrollTopAdjustment += height - previousHeight;
        }
      }
    }

    const newScrollTop = Math.max(
      (scrollTopOverride ?? listState.scrollTopPx) + scrollTopAdjustment,
      0,
    );

    if (rootRef.current!.scrollTop !== newScrollTop) {
      // Make sure the real scrollport is actually in sync with our calculated
      // scrollport. If we took measurements `newScrollTop` will almost
      // certainly be different from `scrollTopPx` and the root's `scrollTop`.
      //
      // It's also possible for the root's `scrollTop` to be out of sync with
      // `scrollTopPx` during the chain of updates which occur when pinning an
      // item. When an item near the end of the list is pinned and new
      // measurements are taken, `newScrollTop` can be greater than what the
      // browser allows an element's `scrollTop` to be (the element's total
      // height minus the scrollport's height). When we set the root's
      // `scrollTop` to `newScrollTop` the browser will clamp that value because
      // we haven't had the chance to re-render with the new measurements so the
      // root is still using the old, smaller total height. It's not until the
      // next render, triggered by updating `scrollTopPx` below, that React will
      // update the root's height. Since the browser already clamped
      // `scrollTop`, we need to set it again so the real scrollport aligns with
      // our calculated scrollport.
      rootRef.current!.scrollTop = newScrollTop;
    }

    if (newScrollTop !== listState.scrollTopPx) {
      setListState((prev) => ({
        ...prev,
        scrollTopPx: newScrollTop,
      }));
    }

    if (newMeasurements != null) {
      setListState((prev) => ({
        ...prev,
        previousMeasurements: newPreviousMeasurements!,
        measurements: newMeasurements,
      }));
    }

    if (didPinChange) {
      setListState((prev) => ({
        ...prev,
        pinnedItemIndex,
        repinKey,
      }));
    }
  });

  // Eviction is handled in a regular Effect rather than a Layout Effect because
  // freeing up memory doesn't effect what the user sees on screen so it can be
  // deferred.
  useEffect(
    function evictUnusedMeasurements() {
      setListState((prev) => {
        const { previousMeasurements, measurements } = prev;

        // As `items` changes, we'll end up accumulating measurements that don't
        // correspond to any available item. We need to evict those measurements
        // to avoid a memory leak. I don't think we can avoid looping over
        // `items` at least once, so below we loop over each item and carry its
        // measurement forward if we find one. Once we're done looping, any
        // unused measurements won't be in the new maps.
        const newPreviousMeasurements: MeasurementMap = new Map();
        const newMeasurements: MeasurementMap = new Map();
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          const key = getItemKey(item);

          const previousMeasurement = previousMeasurements.get(key);
          if (previousMeasurement != null) {
            newPreviousMeasurements.set(key, previousMeasurement);

            // `previousMeasurements` and `measurements` are disjoint so we
            // can go ahead and continue looping since this item definitely
            // isn't in `measurements`.
            continue;
          }

          const measurement = measurements.get(key);
          if (measurement != null) {
            newMeasurements.set(key, measurement);
          }
        }

        // We want to opt out of updating the state if nothing changed, which
        // in this case means every measurement in the maps corresponded to an
        // active item. We only added to the new maps when an item's entry was
        // found in the relevant existing map, so if their sizes match it means
        // every measurement in the existing map corresponded to an active item
        // so nothing needs to change.
        const didPreviousMeasurementsChange =
          newPreviousMeasurements.size !== previousMeasurements.size;

        const didMeasurementsChange =
          newMeasurements.size !== measurements.size;

        if (didPreviousMeasurementsChange || didMeasurementsChange) {
          return {
            ...prev,
            previousMeasurements: didPreviousMeasurementsChange
              ? newPreviousMeasurements
              : previousMeasurements,
            measurements: didMeasurementsChange
              ? newMeasurements
              : measurements,
          };
        } else {
          return prev;
        }
      });
    },
    [items, getItemKey],
  );

  return (
    <Root
      ref={rootRef}
      onScroll={handleScroll}
      style={{ height: scrollportHeight }}
    >
      <List style={{ width: scrollportWidth, height: totalHeight }}>
        {measureLineHeight ? (
          <ListItem data-sentinel>0</ListItem>
        ) : (
          materializedItems.map(({ key, index, top, item, prerender }) => (
            <ListItem
              key={key}
              {...(prerender != null && { "data-prerender": prerender })}
              style={{ top }}
              ref={(node) => {
                if (node == null) {
                  listItemsRef.current.delete(key);
                } else {
                  listItemsRef.current.set(key, node);
                }
              }}
            >
              {renderItem(item, index)}
            </ListItem>
          ))
        )}
      </List>
    </Root>
  );
}

interface MaterializedItem<TItem> {
  key: React.Key;
  index: number;
  top: number;
  item: TItem;
  prerender: "before" | "after" | null;
}

function processItems<TItem>(
  listState: VirtualizedListState,
  scrollportHeight: number,
  items: ReadonlyArray<TItem>,
  pinnedItemIndex: Maybe<number>,
  prerenderCount: number,
  getItemKey: (item: TItem) => React.Key,
): {
  totalHeight: number;
  scrollTopOverride: number | null;
  bottomOffset: number;
  materializedItems: ReadonlyArray<MaterializedItem<TItem>>;
} {
  const { lineHeightPx, scrollTopPx, previousMeasurements, measurements } =
    listState;

  const materializedItems = new Array<MaterializedItem<TItem>>();

  function getItemHeight(key: React.Key): number {
    return (
      measurements.get(key) ?? previousMeasurements.get(key) ?? lineHeightPx
    );
  }

  let totalHeight = 0;
  let scrollTopOverride: number | null = null;
  let bottomOffset = 0;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const key = getItemKey(item);
    const itemHeight = getItemHeight(key);

    if (pinnedItemIndex != null) {
      if (i === pinnedItemIndex) {
        // We've reached the pinned item and need to start working backwards
        // to figure out what should populate the scrollport.

        scrollTopOverride = Math.max(
          totalHeight + itemHeight - scrollportHeight,
          0,
        );

        let remainingHeight = Math.min(
          totalHeight + itemHeight,
          scrollportHeight,
        );

        bottomOffset = Math.max(scrollportHeight - remainingHeight, 0);

        for (let j = i; j >= 0 && remainingHeight > 0; j--) {
          const item = items[j];
          const key = getItemKey(item);
          const itemHeight = getItemHeight(key);

          remainingHeight -= itemHeight;

          materializedItems.unshift({
            key,
            index: j,
            top: scrollTopOverride + remainingHeight,
            item,
            prerender: null,
          });
        }
      }
    } else {
      const isEndAboveScrollportStart = scrollTopPx < totalHeight + itemHeight;
      const isStartBelowScrollportEnd =
        totalHeight < scrollTopPx + scrollportHeight;

      if (isEndAboveScrollportStart && isStartBelowScrollportEnd) {
        materializedItems.push({
          key,
          index: i,
          top: totalHeight,
          item,
          prerender: null,
        });
      }
    }

    totalHeight += itemHeight;
  }

  if (materializedItems.length === 0) {
    return { totalHeight, scrollTopOverride, bottomOffset, materializedItems };
  }

  const firstItem = materializedItems.at(0)!;
  for (
    let offset = 0, runningHeight = firstItem.top;
    offset < prerenderCount;
    offset++
  ) {
    const index = firstItem.index - 1 - offset;

    if (index < 0) {
      break;
    }

    const item = items[index];
    const key = getItemKey(item);
    const itemHeight = getItemHeight(key);

    runningHeight -= itemHeight;

    materializedItems.unshift({
      key,
      index,
      top: runningHeight,
      item,
      prerender: "before",
    });
  }

  const lastItem = materializedItems.at(-1)!;
  const lastItemHeight = getItemHeight(lastItem.key);
  for (
    let offset = 0, runningHeight = lastItem.top + lastItemHeight;
    offset < prerenderCount;
    offset++
  ) {
    const index = lastItem.index + 1 + offset;

    if (index >= items.length) {
      break;
    }

    const item = items[index];
    const key = getItemKey(item);
    const itemHeight = getItemHeight(key);

    materializedItems.push({
      key,
      index,
      top: runningHeight,
      item,
      prerender: "after",
    });

    runningHeight += itemHeight;
  }

  return { totalHeight, scrollTopOverride, bottomOffset, materializedItems };
}
